import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { RouterModule, ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// primeng
import { ButtonModule } from 'primeng/button';
import { SkeletonModule } from 'primeng/skeleton';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { TabsModule } from 'primeng/tabs';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { PaginatorModule } from 'primeng/paginator';

// app
import { PageTitleComponent, SectionCardComponent, SpinnerComponent } from '../../shared/shared.component';
import { LookupService } from '../../../services/lookup.service';
import { SearchResult } from '../../../api/search/search-result';
import { HighlightPipe } from '../../../shared/pipe/highlight.pipe';
import { toPTDateString } from '../../../shared/utils';
import { EntityType } from '../../../api/enum/entity-type';

@Component({
  selector: 'app-advanced-search',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ButtonModule,
    PageTitleComponent,
    SectionCardComponent,
    SkeletonModule,
    TabsModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    PaginatorModule,
    HighlightPipe,
    SpinnerComponent,
    ToastModule,
  ],
  templateUrl: './advanced-search.component.html',
  styleUrls: ['./advanced-search.component.scss'],
  providers: [LookupService, MessageService],
})
export class AdvancedSearchComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly searchAbort$ = new Subject<void>();

  EntityType = EntityType;
  toPTDateString = toPTDateString;
  searchText = '';
  searchedKeyword = ''; // Store the last searched keyword
  activeTab = -1;
  searchResult: SearchResult | null = null;
  pageSize: number = 5;
  currentPage: number = 0;
  totalRecords: number = 0;
  loading: boolean = false;

  tabs = [
    { label: 'All', value: -1 },
    { label: 'Foreign National', value: EntityType.ForeignNational },
    { label: 'Matter', value: EntityType.Matter },
    { label: 'Company', value: EntityType.Company },
    { label: 'Corp User', value: EntityType.CompanyContact },
    { label: 'Location', value: EntityType.CompanyLocation },
  ];

  get hasResults(): boolean {
    return !!(this.searchResult?.companies?.length || this.searchResult?.matters?.length || this.searchResult?.foreignNationals?.length || this.searchResult?.contacts?.length || this.searchResult?.locations?.length);
  }

  constructor(private readonly lookupService: LookupService, private readonly route: ActivatedRoute, private readonly messageService: MessageService) {}

  ngOnInit() {
    // Handle URL query parameters
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      if (params['q']) {
        this.searchText = params['q'];
        this.performSearch();
      }
    });
  }

  onSearch() {
    this.currentPage = 0;
    this.performSearch();
  }

  onPageChange(event: any) {
    this.currentPage = event.page;
    this.pageSize = event.rows;
    this.performSearch();
  }

  joinValues(...values: (string | undefined | null)[]): string {
    return values.filter((value) => value?.trim()).join(' | ');
  }

  private performSearch() {
    // Cancel previous search if any
    this.searchAbort$.next();
    this.loading = false;

    if (!this.searchText.trim() || this.searchText.trim().length < 2) {
      this.searchResult = null;
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Please enter at least 2 characters',
      });
      return;
    }

    // Store the keyword used for search
    this.searchedKeyword = this.searchText;

    this.loading = true;
    const entityType = this.activeTab === -1 ? null : this.activeTab;

    this.lookupService
      .search({
        filter: this.searchText,
        pageSizeByType: this.pageSize,
        pageIndex: this.currentPage,
        entityType,
        getPageCount: true,
      })
      .pipe(takeUntil(this.searchAbort$), takeUntil(this.destroy$))
      .subscribe({
        next: (result: SearchResult) => {
          this.searchResult = result;
          this.totalRecords = result.pageCount * this.pageSize;
          this.loading = false;
        },
        error: () => {
          this.loading = false;
        },
      });
  }

  private getEntityType(tabValue: string): number | null {
    return tabValue === '0' ? null : Number(tabValue);
  }

  ngOnDestroy() {
    this.searchAbort$.next();
    this.searchAbort$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
